import React from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const {
  FiHome,
  FiBook,
  FiTrendingUp,
  FiUser,
  FiLogOut,
  FiUsers,
  FiBookOpen,
  FiSettings,
  FiLayers,
  FiUpload,
  FiClipboard,
  FiCalendar,
  FiFileText,
  FiAward,
  FiStar,
  FiActivity,
  FiCode,
  FiHeart,
  FiGrid
} = FiIcons

const Sidebar = () => {
  const { signOut, userProfile } = useAuth()
  const navigate = useNavigate()

  const handleSignOut = async () => {
    await signOut()
    navigate('/')
  }

  // Role-specific navigation items
  const getNavSections = () => {
    switch (userProfile?.role) {
      case 'student':
        return [{
          items: [
            { path: '/dashboard', icon: FiHome, label: 'Home' },
            { path: '/classes', icon: FiBook, label: 'My Classes' },
            { path: '/progress', icon: FiTrendingUp, label: 'Progress' },
            { path: '/activity-logs', icon: FiActivity, label: 'Activity Logs' },
            { path: '/friday-showcase', icon: FiStar, label: 'Friday Showcase' },
            { path: '/enrichment-blocks', icon: FiCode, label: 'Enrichment Blocks' },
            { path: '/faith-growth', icon: FiHeart, label: 'Faith & Growth' },
            { path: '/scope-sequence', icon: FiGrid, label: 'Scope & Sequence' },
            { path: '/profile', icon: FiUser, label: 'Profile' },
          ]
        }]
      case 'parent':
        return [
          {
            items: [
              { path: '/dashboard', icon: FiHome, label: 'Dashboard' },
              { path: '/my-children', icon: FiUsers, label: 'My Children' },
              { path: '/activity-logs', icon: FiActivity, label: 'Activity Logs' },
              { path: '/friday-showcase', icon: FiStar, label: 'Friday Showcase' },
              { path: '/scope-sequence', icon: FiGrid, label: 'Scope & Sequence' },
              { path: '/settings', icon: FiSettings, label: 'Settings' },
            ]
          },
          {
            label: 'Curriculum Builder',
            items: [
              { path: '/curricula', icon: FiBookOpen, label: 'Curricula' },
              { path: '/curriculum-templates', icon: FiLayers, label: 'Templates' },
              { path: '/curriculum-import-export', icon: FiUpload, label: 'Import/Export' },
            ]
          },
          {
            label: 'Records',
            items: [
              { path: '/gradebook', icon: FiClipboard, label: 'Gradebook' },
              { path: '/attendance', icon: FiCalendar, label: 'Attendance' },
              { path: '/transcripts', icon: FiFileText, label: 'Transcripts' },
              { path: '/certificates', icon: FiAward, label: 'Certificates' },
            ]
          },
          {
            label: 'Enrichment',
            items: [
              { path: '/enrichment-blocks', icon: FiCode, label: 'Enrichment Blocks' },
              { path: '/faith-growth', icon: FiHeart, label: 'Faith & Growth' },
            ]
          }
        ]
      case 'teacher':
        return [
          {
            items: [
              { path: '/dashboard', icon: FiHome, label: 'Dashboard' },
              { path: '/students', icon: FiUsers, label: 'Students' },
              { path: '/lessons', icon: FiBook, label: 'Lessons' },
              { path: '/teacher-hub', icon: FiClipboard, label: 'Teacher Hub' },
              { path: '/scope-sequence', icon: FiGrid, label: 'Scope & Sequence' },
              { path: '/profile', icon: FiUser, label: 'Profile' },
            ]
          },
          {
            label: 'Curriculum Builder',
            items: [
              { path: '/curricula', icon: FiBookOpen, label: 'Curricula' },
              { path: '/curriculum-templates', icon: FiLayers, label: 'Templates' },
              { path: '/curriculum-import-export', icon: FiUpload, label: 'Import/Export' },
            ]
          },
          {
            label: 'Records',
            items: [
              { path: '/assessments', icon: FiTrendingUp, label: 'Assessments' },
              { path: '/gradebook', icon: FiClipboard, label: 'Gradebook' },
              { path: '/attendance', icon: FiCalendar, label: 'Attendance' },
              { path: '/transcripts', icon: FiFileText, label: 'Transcripts' },
              { path: '/certificates', icon: FiAward, label: 'Certificates' },
            ]
          },
          {
            label: 'Enrichment',
            items: [
              { path: '/activity-logs', icon: FiActivity, label: 'Activity Logs' },
              { path: '/friday-showcase', icon: FiStar, label: 'Friday Showcase' },
              { path: '/enrichment-blocks', icon: FiCode, label: 'Enrichment Blocks' },
              { path: '/faith-growth', icon: FiHeart, label: 'Faith & Growth' },
            ]
          }
        ]
      case 'administrator':
        return [{
          items: [
            { path: '/dashboard', icon: FiHome, label: 'Dashboard' },
            { path: '/admin-panel', icon: FiUsers, label: 'Admin Panel' },
            { path: '/scope-sequence', icon: FiGrid, label: 'Scope & Sequence' },
            { path: '/settings', icon: FiSettings, label: 'Settings' },
          ]
        }]
      default:
        return [{
          items: [
            { path: '/dashboard', icon: FiHome, label: 'Home' },
            { path: '/profile', icon: FiUser, label: 'Profile' },
          ]
        }]
    }
  }

  const navSections = getNavSections()

  return (
    <div className="bg-white shadow-lg h-full w-64 flex flex-col">
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <h1 className="text-2xl font-bold text-[#1B4C87]" style={{ fontFamily: 'Dancing Script' }}>
          Prosperity
        </h1>
        <p className="text-sm text-gray-600 mt-1">HomeSchool Academy</p>
        {userProfile && (
          <div className="mt-3 p-3 bg-[#00AAFF]/10 rounded-lg">
            <p className="text-sm font-medium text-[#1B4C87]">
              {userProfile.first_name} {userProfile.last_name}
            </p>
            <p className="text-xs text-gray-600 capitalize">{userProfile.role}</p>
          </div>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4">
        <div className="space-y-6">
          {navSections.map((section, sectionIndex) => (
            <div key={section.label || sectionIndex}>
              {section.label && (
                <p className="px-4 mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  {section.label}
                </p>
              )}
              <ul className="space-y-2">
                {section.items.map((item) => (
                  <li key={item.path}>
                    <NavLink
                      to={item.path}
                      className={({ isActive }) =>
                        `flex items-center px-4 py-3 rounded-lg transition-colors ${
                          isActive
                            ? 'bg-[#00AAFF] text-white'
                            : 'text-gray-700 hover:bg-gray-100'
                        }`
                      }
                    >
                      <SafeIcon icon={item.icon} className="w-5 h-5 mr-3" />
                      {item.label}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </nav>

      {/* Sign Out */}
      <div className="p-4 border-t border-gray-200">
        <button
          onClick={handleSignOut}
          className="flex items-center w-full px-4 py-3 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <SafeIcon icon={FiLogOut} className="w-5 h-5 mr-3" />
          Sign Out
        </button>
      </div>
    </div>
  )
}

export default Sidebar
